import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLocationContext } from '../hooks/useLocationContext';
import { ScopedLink, useScopedPath } from '../components/ScopedLink';
import { useLocalGetStudentsByParentQuery, useGetDashboardStatsQuery } from '../store/api/localApi';
import RoleGate from '../components/RoleGate';
import RegistrationStatusBadge from '../components/RegistrationStatusBadge';
import { studentDisplayName } from '../utils/student';
import { formatDublinDate, formatCivilDate } from '../utils/date';
import { brand } from '../branding';
import {
  AcademicCapIcon,
  BuildingOfficeIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import ComplianceCard from '../components/fire-drills/ComplianceCard';
import NotifiableOverdueBanner from '../components/incidents/NotifiableOverdueBanner';
import LiveSessionsStrip from '../components/attendance/LiveSessionsStrip';
import StaffComplianceExpiringTile from '../components/staff-compliance/StaffComplianceExpiringTile';

const Dashboard: React.FC = () => {
  const { user, activeRole, isParent, isStaffOrAbove, isAdmin, isOrgAdmin, isSuperAdmin } =
    useAuth();
  const {
    effectiveLocationId: activeLocationId,
    effectiveOrgId,
    isInLocation,
    isInOrg,
  } = useLocationContext();
  const scopedPath = useScopedPath();

  // Determine the effective role for dashboard stats based on navigation context
  // When org/super admin navigates into a location, they act as LOCATION_ADMIN for stats
  const effectiveStatsRole = (() => {
    if (isInLocation && (isSuperAdmin || isOrgAdmin)) return 'LOCATION_ADMIN';
    if (isInLocation) return activeRole;
    if (isInOrg && isSuperAdmin) return 'ORG_ADMIN';
    return activeRole;
  })();

  const { data: stats } = useGetDashboardStatsQuery(
    { locationId: activeLocationId, role: effectiveStatsRole, organizationId: effectiveOrgId },
    { skip: !activeRole }
  );

  const { data: userStudents } = useLocalGetStudentsByParentQuery(user?.id || '', {
    skip: !user?.id || !isParent,
  });

  const statCards = [
    {
      name: 'My Children',
      value: stats?.myChildren ?? userStudents?.length ?? 0,
      icon: AcademicCapIcon,
      color: 'bg-blue-500',
      visible: isParent,
      link: scopedPath('/students'),
    },
    {
      name: 'Students',
      value: stats?.totalStudents ?? 0,
      icon: UserGroupIcon,
      color: 'bg-purple-500',
      visible: isStaffOrAbove && isInLocation,
      link: scopedPath('/students'),
    },
    {
      name: 'Classes',
      value: stats?.totalClasses ?? 0,
      icon: ClipboardDocumentCheckIcon,
      color: 'bg-indigo-500',
      visible: isStaffOrAbove && isInLocation,
      // The Classes stat links to the class LIST (/classes), matching the
      // sidebar "Classes" nav — it wrongly pointed at /calendar since the
      // ScopedLink migration (4e83c08), sending "Classes" to the calendar.
      link: scopedPath('/classes'),
    },
    {
      name: 'Families',
      value: stats?.totalFamilies ?? 0,
      icon: UserGroupIcon,
      color: 'bg-yellow-500',
      visible: isAdmin && isInLocation,
      link: scopedPath('/students'),
    },
    {
      name: 'Locations',
      value: stats?.totalLocations ?? 0,
      icon: BuildingOfficeIcon,
      color: 'bg-green-500',
      visible: (isOrgAdmin || isSuperAdmin) && isInOrg && !isInLocation,
      link: scopedPath('/locations'),
    },
    {
      name: 'Organizations',
      value: stats?.totalOrganizations ?? 0,
      icon: BuildingLibraryIcon,
      color: 'bg-rose-500',
      visible: isSuperAdmin && !isInOrg,
      link: '/organizations',
    },
  ].filter((stat) => stat.visible);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              {getGreeting()}, {user?.firstName}!
            </h2>
            <p className="mt-1 text-sm text-gray-500">Welcome to your {brand.name} dashboard</p>
          </div>
        </div>

        {stats?.notifiableOverdue && (
          <div className="mt-6">
            <NotifiableOverdueBanner />
          </div>
        )}

        {/* Live sessions — the "walk back into what's running" board. Staff/admin
            in a location only; scoped by role server-side (admins see all, staff
            only their assigned classes). Renders nothing when nothing is live. */}
        {isStaffOrAbove && isInLocation && activeLocationId && (
          <LiveSessionsStrip locationId={activeLocationId} />
        )}

        {/* Stats */}
        <div className="mt-8">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {statCards.map((stat) => (
              <Link
                key={stat.name}
                to={stat.link}
                data-testid={`stat-${stat.name.toLowerCase().replace(/\s+/g, '-')}`}
                className="relative bg-white pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden hover:shadow-md transition-shadow"
              >
                <div>
                  <div className={`absolute rounded-md p-3 ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                  <p className="ml-16 text-sm font-medium text-gray-500 truncate">{stat.name}</p>
                </div>
                <div className="ml-16 flex items-baseline pb-6 sm:pb-7">
                  <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Leaving soon: informational list of children whose announced
            departure lands within 60 days (spec §3). Backend only populates
            this for staff/admin roles; render nothing when the list is empty
            so an ordinary day shows no widget at all. */}
        {isStaffOrAbove && !!stats?.leavingSoon?.length && (
          <div className="mt-8">
            <div
              className="bg-white shadow rounded-lg overflow-hidden"
              data-testid="dashboard-leaving-soon"
            >
              <div className="px-4 py-3 border-b border-gray-100">
                <h3 className="text-sm font-medium text-gray-900">Leaving in the next 60 days</h3>
                <p className="mt-0.5 text-xs text-gray-500">
                  Children with a planned last day coming up. For your awareness — no action needed.
                </p>
              </div>
              <ul className="divide-y divide-gray-100">
                {stats.leavingSoon.map((child) => (
                  <li
                    key={child.id}
                    data-testid={`leaving-soon-row-${child.id}`}
                    className="px-4 py-2.5 flex items-center justify-between"
                  >
                    <ScopedLink
                      to={`/students/${child.id}`}
                      className="text-sm font-medium text-gray-900 hover:text-primary-600 truncate"
                    >
                      {child.name}
                    </ScopedLink>
                    <span className="text-sm text-gray-500 whitespace-nowrap ml-3">
                      {formatCivilDate(child.expectedEndDate)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Staff compliance: one neutral count, items expiring within 60
            days. Admin-only (same as the register it's derived from) and
            location-scoped — no "missing"/"not recorded" language, no red,
            no alarm styling. See StaffComplianceExpiringTile for why this
            can't just read a stat off useGetDashboardStatsQuery. */}
        {isAdmin && isInLocation && activeLocationId && (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StaffComplianceExpiringTile locationId={activeLocationId} />
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-8">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Quick Actions</h3>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <RoleGate allow={['PARENT']}>
              <QuickAction
                to={scopedPath('/students')}
                icon={AcademicCapIcon}
                color="text-blue-500"
                title="View My Children"
                subtitle="See student profiles and information"
              />
            </RoleGate>
            <RoleGate when={isStaffOrAbove && isInLocation}>
              <QuickAction
                to={scopedPath('/students')}
                icon={UserGroupIcon}
                color="text-green-500"
                title="Manage Students"
                subtitle="View all students at your location"
              />
            </RoleGate>
            <RoleGate when={isStaffOrAbove && isInLocation}>
              <QuickAction
                to={scopedPath('/classes')}
                icon={ClipboardDocumentCheckIcon}
                color="text-indigo-500"
                title="Take Attendance"
                subtitle="Run today's session from the class list"
              />
            </RoleGate>
            <RoleGate when={isAdmin && isInLocation}>
              <QuickAction
                to={scopedPath('/forms')}
                icon={DocumentTextIcon}
                color="text-orange-500"
                title="Manage Forms"
                subtitle="Create and distribute forms"
              />
            </RoleGate>
            <RoleGate when={(isOrgAdmin || isSuperAdmin) && isInOrg && !isInLocation}>
              <QuickAction
                to={scopedPath('/locations')}
                icon={BuildingOfficeIcon}
                color="text-purple-500"
                title="Manage Locations"
                subtitle="View and manage organization locations"
              />
            </RoleGate>
            <RoleGate when={isSuperAdmin && !isInOrg}>
              <QuickAction
                to="/organizations"
                icon={BuildingLibraryIcon}
                color="text-rose-500"
                title="Manage Organizations"
                subtitle="View and manage all organizations"
              />
            </RoleGate>
          </div>
        </div>

        {/* Compliance — location-scoped staff/admin only */}
        {isInLocation && isStaffOrAbove && activeLocationId && (
          <ComplianceCard locationId={activeLocationId} />
        )}

        {/* My Children list */}
        <RoleGate when={isParent && !!userStudents?.length}>
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <h3 className="text-lg leading-6 font-medium text-gray-900">My Children</h3>
              <ScopedLink
                to="/students"
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                View all
              </ScopedLink>
            </div>
            <div className="mt-4 bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {userStudents?.slice(0, 3).map((student) => (
                  <li key={student.id}>
                    <ScopedLink
                      to={`/students/${student.id}`}
                      className="block hover:bg-gray-50 px-4 py-4 sm:px-6"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-primary-600">
                                {student.firstName?.[0] ?? '?'}
                                {student.lastName?.[0] ?? ''}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="text-sm font-medium text-gray-900">
                                {studentDisplayName(student)}
                              </div>
                              <RegistrationStatusBadge student={student} />
                            </div>
                            <div className="text-sm text-gray-500">
                              {student.grade ? `${student.grade}` : 'Awaiting registration'}
                              {student.dateOfBirth &&
                                ` • Born ${formatDublinDate(student.dateOfBirth)}`}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-gray-400">View details →</div>
                      </div>
                    </ScopedLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </RoleGate>
      </div>
    </div>
  );
};

/** Reusable quick action card */
const QuickAction: React.FC<{
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  color: string;
  title: string;
  subtitle: string;
}> = ({ to, icon: Icon, color, title, subtitle }) => (
  <Link
    to={to}
    data-testid={`action-${title.toLowerCase().replace(/\s+/g, '-')}`}
    className="relative rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm hover:border-gray-400 hover:shadow-md transition-all"
  >
    <div className="flex items-center space-x-3">
      <div className="flex-shrink-0">
        <Icon className={`h-8 w-8 ${color}`} />
      </div>
      <div className="min-w-0 flex-1">
        <span className="absolute inset-0" />
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>
    </div>
  </Link>
);

export default Dashboard;
